import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Spacing, Text } from '@reservamos/elements';
import useWhitelabelCopies from 'hooks/whitelabel/useWhitelabelCopies';
import LocalizedCurrency from 'components/LocalizedCurrency';
import CheckList from '../../../../ui/atoms/CheckList';
import PaymentImage from '../../../../ui/atoms/PaymentImage';
import { formatDeadline, groupReference, tArray } from '../paymentFormatUtils';

/**
 * ConektaVoucher.
 * OXXO (OXXOPay) printable store-payment voucher, ported from frontline-platform's
 * `conekta_payment_format` template. Shows the amount, the dash-grouped reference (and barcode when
 * present), the payment deadline in the payment timezone, the OXXO instructions, and — for brands
 * that define them (`gfa`, `ttur`) — the purchase conditions.
 *
 * @returns {JSX.Element} The OXXO voucher.
 */
const ConektaVoucher = () => {
  const { t } = useTranslation('payment');
  const { reference, barcodeUrl, expirationDate } = useSelector((state) => state.payment.toJS());
  const { total } = useSelector((state) => state.purchase.toJS());
  const { brand } = useSelector((state) => state.whitelabelConfig.env);
  const { provider } = useWhitelabelCopies();

  const deadline = formatDeadline(expirationDate);
  const conditions = tArray(t, `payment_format.oxxo_conditions.${brand}`);

  return (
    <Spacing vertical size="M">
      <PaymentImage type="oxxo-logo" />

      <Spacing vertical size="S" alignItems="center">
        <Text size="M" weight="semibold">
          {t('payment_format.amount_to_pay')}
        </Text>
        {/*
          Decimals are pinned to 2 instead of inheriting the tenant's `tripPriceDecimals`: that
          config tunes how trip prices are displayed, but this is the exact cash amount the
          passenger hands over at the store, so it must always show cents (a tenant configured
          with fewer decimals rendered "$132.0").
        */}
        <LocalizedCurrency
          price={total}
          currencyStyle="currency"
          decimals={2}
          size="XL"
          weight="bold"
        />
        <Text size="S" color="grayMedium" textAlign="center">
          {t('payment_format.oxxo_commission_note')}
        </Text>
      </Spacing>

      <Spacing vertical size="S" alignItems="center">
        <Text size="S" weight="semibold">
          {t('payment_format.reference')}
        </Text>
        {Boolean(barcodeUrl) && <img src={barcodeUrl} alt={t('payment_format.reference')} />}
        <Text size="XL" weight="bold" textAlign="center">
          {groupReference(reference)}
        </Text>
      </Spacing>

      <Spacing vertical size="S" alignItems="center">
        <Text size="S" weight="semibold">
          {t('payment_format.deadline')}
        </Text>
        <Text size="M" textAlign="center">
          {t('payment_format.before_date', { date: deadline.date, time: deadline.time })}
        </Text>
      </Spacing>

      <CheckList
        title={t('payment_format.instructions_title')}
        labels={tArray(t, 'payment_format.oxxo_instructions')}
      />

      <Text size="S" color="grayMedium">
        {t('payment_format.oxxo_footnote', { brand: provider.name })}
      </Text>

      {conditions.length > 0 && (
        <CheckList title={t('payment_format.conditions_title')} labels={conditions} />
      )}
    </Spacing>
  );
};

export default ConektaVoucher;
